import styled from 'styled-components';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';
import { FIELDS_KEYS } from '_constants/forms';
import { hexToRGBA } from '_helpers/theme';
import { createQueryString } from '_helpers/urls';

import { subscribe } from '_redux/tips/slice';
import { selectResult, selectError, selectLoading } from '_redux/tips/selector';
import { selectFormValues as selectFilingFormValues } from '_redux/order/selector';

import * as Layout from '_components/atomic/layouts';
import SubscribeForTipsForm from '../SubscribeForTipsForm';

const StyledOuterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: ${theme.spaces.large6X}px;
  padding-bottom: ${theme.spaces.large6X}px;
  margin-bottom: ${theme.spaces.large12X}px;
  border-top: 1px solid ${theme.colors.gray[200]};
  border-bottom: 1px solid ${theme.colors.gray[200]};

  @media ${theme.devices.tablet} {
    padding-top: ${theme.spaces.largeX}px;
    padding-bottom: ${theme.spaces.largeX}px;
    margin-bottom: 0;
  }
`;

const StyledInnerContainer = styled.div`
  position: relative;
  width: 50%;

  @media ${theme.devices.tablet} {
    width: 100%;
  }
`;

const StyledImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media ${theme.devices.tablet} {
    img {
      width: 100%;
    }
  }
`;

const StyledContentBox = styled.div`
  position: relative;

  @media ${theme.devices.tablet} {
    margin-bottom: ${theme.spaces.large}px;
  }
`;

const StyledAbsoluteContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const StyledCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.small}px;
  border-radius: 6px;
  background: ${hexToRGBA(theme.colors.black, 0.65)};

  > * {
    line-height: 100%;
  }
`;

const StyledFormContainer = styled.div`
  position: absolute;
  top: 30%;
  right: -20%;
  max-width: 240px;
  width: 100%;
  padding-top: ${theme.spaces.regular}px;
  padding-bottom: ${theme.spaces.regular}px;
  padding-left: ${theme.spaces.largeX}px;
  padding-right: ${theme.spaces.largeX}px;
  border: 1px solid ${theme.colors.gray[200]};
  border-radius: 10px;
  box-shadow: 0px 4px 4px 0px #00000040;
  background: ${theme.colors.white};

  @media ${theme.devices.tablet} {
    position: static;
    max-width: unset;
  }
`;

const StyledText = styled.div`
  display: inline;
  text-shadow: -1px 0 ${theme.colors.white}, 0 1px ${theme.colors.white},
    1px 0 ${theme.colors.white}, 0 -1px ${theme.colors.white};
`;

const StyledLink = styled.div`
  padding-top: ${theme.spaces.small2X}px;
  padding-bottom: ${theme.spaces.small2X}px;
  padding-left: ${theme.spaces.large}px;
  padding-right: ${theme.spaces.large}px;
  background: ${theme.colors.white};
  border-radius: 4px;
  cursor: pointer;
`;

const StartBanner = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const filingFormValues = useSelector(selectFilingFormValues);
  const result = useSelector(selectResult);
  const error = useSelector(selectError);
  const loading = useSelector(selectLoading);

  const handleSubscribe = useCallback(
    (values) => {
      if (values) {
        dispatch(
          subscribe({
            name: values[FIELDS_KEYS.fullName],
            email: values[FIELDS_KEYS.email],
          })
        );
      }
    },
    [dispatch]
  );

  const handleStart = useCallback(() => {
    router.push(
      `${ROUTES.llcIncorporationFiling}${createQueryString({
        step: filingFormValues?.step || 0,
      })}`,
      undefined,
      { shallow: true }
    );
  }, [router, filingFormValues]);

  return (
    <StyledOuterContainer>
      <StyledInnerContainer>
        <StyledContentBox>
          <StyledImageContainer>
            <picture>
              <source
                srcSet={`${config.basePath}/images/blog-start-your-business-now.png`}
                type="image/png"
              />
              <img
                src={`${config.basePath}/images/blog-start-your-business-now.png`}
                alt="Start Forming Your Business Now"
              />
            </picture>
          </StyledImageContainer>
          <StyledAbsoluteContainer>
            <StyledCardContainer>
              <Layout.Card.Text
                size={theme.fontSizes.large4X}
                color={theme.colors.white}
                boldest
                centered
                noBottomSpace
                desktop
              >
                START FORMING YOUR
              </Layout.Card.Text>
              <Layout.Card.Text
                size={theme.fontSizes.large4X}
                color="#333333"
                boldest
                centered
                desktop
              >
                <StyledText>BUSINESS NOW</StyledText>
              </Layout.Card.Text>
              <Layout.Card.Text
                size={theme.fontSizes.largeX}
                color={theme.colors.white}
                boldest
                centered
                noBottomSpace
                mobile
              >
                START FORMING YOUR
              </Layout.Card.Text>
              <Layout.Card.Text
                size={theme.fontSizes.largeX}
                color="#333333"
                boldest
                centered
                mobile
              >
                <StyledText>BUSINESS NOW</StyledText>
              </Layout.Card.Text>
              <StyledLink onClick={handleStart}>
                <Layout.Card.Text
                  size={theme.fontSizes.largeX}
                  color="#333333"
                  boldest
                  centered
                  noBottomSpace
                >
                  REGISTER NOW
                </Layout.Card.Text>
              </StyledLink>
            </StyledCardContainer>
          </StyledAbsoluteContainer>
        </StyledContentBox>
        <StyledFormContainer>
          <SubscribeForTipsForm
            error={error}
            success={result}
            submitDisabled={loading}
            onSubmit={handleSubscribe}
          />
        </StyledFormContainer>
      </StyledInnerContainer>
    </StyledOuterContainer>
  );
};

export default StartBanner;
